import { useEffect, useState } from "react";
import Layout from "../../../components/template/Layout";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import { FeatureFlagWrapper } from "../../../components/FeatureFlag/FeatureFlagWrapper";
import DashboardCard from "./AnalyticsCard";
import { FaClipboardList, FaArrowRight } from "react-icons/fa";
import {
  getInboundItems,
  getHouseLocations,
  getEvents,
} from "../../../redux/slices/InboundItemsSlice";
import { getInboundItemsFromDB } from "../../../db/schemas/inboundItems";
import { InboundItem } from "../../../types/item";
import { fetchWorkOrders } from "../../../redux/slices/workOrderSlice";
import { fetchFeatureFlags } from "../../../redux/slices/authSlice";
import { fetchInspectionTemplates } from "../../../redux/slices/inspectionTemplatesSlice";
import INNOVA_ICON from "/innova-icon-new.png?url";
import { WorkOrderStatus } from "../../../types/workOrder";
import store from "../../../redux/store";
import { ItemStatus } from "../../../types/enums/item";
import PullToRefresh from "react-simple-pull-to-refresh";

const DashboardScreen = () => {
  const { userName, token, userId, featureFlags } = useSelector(
    (state: RootState) => state.auth
  );
  const { workOrders } = useSelector((state: RootState) => state.workOrders) || [];
  const [inboundItems, setInboundItems] = useState<InboundItem[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Helper to check if inbound items feature flag is enabled
  // Uses same flag as FeatureFlagWrapper: 'enableTenantInboundAccess'
  const hasInboundItemsAccess = ()   => {
    if (!featureFlags) return false;
    return featureFlags["enableTenantInboundAccess"] || false;
  };

  const fetchInboundItems = async () => {
    // 1. Fetch from IndexedDB (all items)
    const indexDBItems = (await getInboundItemsFromDB()) || [];

    // 2. Fetch from API (server items)
    await dispatch(getInboundItems()).unwrap();
    const serverItems = (store.getState() as unknown as RootState).inboundItems
      .items;

    // 3. Merge IndexedDB and server items (favor IndexedDB if exists in both)
    const processedItems: InboundItem[] = [];
    const includedServerIds = new Set<number | string>();

    // Add all IndexedDB items (these may have pending changes or be new items)
    for (const indexDBItem of indexDBItems) {
      processedItems.push(indexDBItem);

      // Track which server items we've included
      if (indexDBItem.id && Number(indexDBItem.id) > 0) {
        includedServerIds.add(indexDBItem.id);
      }
    }

    // Add server items that aren't in IndexedDB
    for (const serverItem of serverItems) {
      if (serverItem.id && !includedServerIds.has(serverItem.id)) {
        processedItems.push(serverItem);
      }
    }

    setInboundItems(processedItems);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await dispatch(fetchWorkOrders(token || ""));
      await dispatch(fetchFeatureFlags());
      await dispatch(fetchInspectionTemplates({ token: token || "" }));

      if (hasInboundItemsAccess()) {
        await fetchInboundItems();
        await dispatch(getHouseLocations());
        await dispatch(getEvents());
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Pull-to-refresh: reuse same fetch
  const handleRefresh = async () => {
    await fetchData();
  };

  // Calculate Inbound Items stats
  const pendingInboundItems = inboundItems.filter(
    (item) => item.status_ID === ItemStatus.Pending
  ).length;
  const UploadedInboundItems = inboundItems.filter(
    (item) => item.id && Number(item.id) > 0
  ).length;
  const myInboundItems = inboundItems.filter(
    (item) => item.createdBy_ID === userId
  ).length;

  // Calculate Work Orders stats
  const inProgressWorkOrders = workOrders ? workOrders.filter(
    (wo) => wo.workOrderStatus === WorkOrderStatus.InProgress
  ).length : 0;
  const completedWorkOrders = workOrders ? workOrders.filter(
    (wo) => wo.workOrderStatus === WorkOrderStatus.Completed
  ).length : 0;
  const scheduledWorkOrders = workOrders ? workOrders.filter(
    (wo) => wo.workOrderStatus === WorkOrderStatus.Scheduled
  ).length : 0;

  if (isLoading) {
    return (
      <Layout title="Dashboard" isDarkMode={true}>
        <div className="flex justify-center items-center w-full min-h-[calc(100vh-84px)] bg-gray-50">
          <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      refreshingContent={
        <div className="flex justify-center items-center mt-4">
          <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin"></div>
        </div>
      }
    >
      <Layout title="Dashboard" isDarkMode={true}>
        <div className="w-full min-h-[calc(100vh-84px)] bg-gray-50 p-6">
          <div className="mb-6 flex items-center justify-center">
            <img src={INNOVA_ICON} alt="logo" className="h-16 w-16 mr-4" />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Welcome back!</h1>
              <div className="flex items-center gap-2 w-100">
                <div>
                  <span className="text-gray-800 font-bold">{userName}</span>
                </div>
              </div>
            </div>

            {/* <p className="text-gray-500 mt-2">Here's your dashboard overview</p> */}
          </div>

          {/* Dashboard Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Inbound Items Card */}
            <FeatureFlagWrapper
              flag="enableTenantInboundAccess"
            >
              <DashboardCard
                title="Inbound Items"
                value={inboundItems.length}
                icon={<FaArrowRight className="text-gray-600 text-2xl" />}
                stats={[
                  { label: "Pending", value: pendingInboundItems },
                  { label: "My Uploaded Items", value: myInboundItems },
                  { label: "All Uploaded Items", value: UploadedInboundItems },
                ]}
                buttonText="Quick Create"
                onButtonClick={() => navigate("/inbound-items")}
              />
            </FeatureFlagWrapper>

            {/* Work Orders Card */}
            <DashboardCard
              title="Work Orders"
              value={workOrders ? workOrders.length : 0}
              icon={<FaClipboardList className="text-gray-600 text-2xl" />}
              stats={[
                { label: "Scheduled", value: scheduledWorkOrders },
                { label: "In Progress", value: inProgressWorkOrders },
                { label: "Completed", value: completedWorkOrders },
              ]}
              buttonText="View Work Orders"
              onButtonClick={() => navigate("/work-orders")}
            />
          </div>
        </div>
      </Layout>
    </PullToRefresh>
  );
};

export default DashboardScreen;
